import { HeroStatisticsItem, Text } from '@ahc-digital/component-library';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { PropsWithChildren, ReactNode } from 'react';
import { PromptForMissingDatasource } from 'src/shared/PromptForMissingDatasource';
import { CreateHeroStatisticsItemModel } from './C52HeroStatisticsItem.service';
import { C52HeroStatisticsItemProps } from './C52HeroStatisticsItem.types';
import { translationClasses } from 'src/shared/TranslationClasses';
import { useCreateMarketingRenderingHame } from 'src/shared/useCreateMarketingRenderingName';

/** The component-library component for this rendering does not display in a way that makes editing in Experience Editor feasible.
 * So `EditHeroStatisticsItem` is a custom component that displays the fields with minimal styles.
 */
const EditHeroStatisticsItem = (
  props?: PropsWithChildren & { fields: Array<{ label: string; field: ReactNode }> }
) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.5rem' }}>
      {props?.fields.map((f, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Text tag="span" style={{ textAlign: 'left' }}>
            <b>{f.label}</b>
          </Text>
          <Text tag="span" style={{ textAlign: 'right', flexShrink: '0' }}>
            {f.field}
          </Text>
        </div>
      ))}
    </div>
  );
};

export const Default = (props?: C52HeroStatisticsItemProps): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const isEditing = sitecoreContext.pageEditing ?? false;
  const marketingRenderingName = useCreateMarketingRenderingHame(
    props?.rendering?.componentName as string
  );

  if (!props?.rendering?.dataSource) {
    return <PromptForMissingDatasource rendering={props?.rendering} />;
  }

  const model = CreateHeroStatisticsItemModel(props);

  if (isEditing) {
    return (
      <EditHeroStatisticsItem
        fields={[
          { label: 'Top text', field: model.topText },
          { label: 'Prefix', field: model.prefix },
          { label: 'Statistics number', field: model.statisticsNumber },
          { label: 'Suffix', field: model.suffix },
          {
            label: 'Superscript suffix',
            field: model.superscriptSuffix,
          },
          { label: 'Bottom text', field: model.bottomText },
        ]}
      />
    );
  }

  return (
    <HeroStatisticsItem
      className={translationClasses(props?.params)}
      id={props?.params?.AnchorLinkTargetName}
      data-component={marketingRenderingName}
      {...model}
    />
  );
};
